import jsPDF from "jspdf";
import type {
  RenderedCheck,
  RenderedCheckElement,
} from "../CheckPrinting.Store";
import { downloadFile } from "../../shared/downloadFile";

/**
 * Template configuration from API response
 */
export interface TemplateConfig {
  layoutType: string;
  stubTopHeight: number;
  checkHeight: number;
  stubBottomHeight: number;
  pageHeight: number;
  marginTop: number;
  marginLeft: number;
  marginRight: number;
  offsetX: number;
  offsetY: number;
}

/**
 * PDF generation options
 */
export interface PDFGeneratorOptions {
  showTestWatermark?: boolean;
  pageWidth?: number; // Default 8.5 inches
  pageHeight?: number; // Default 11 inches
}

/**
 * CheckPDFGenerator
 *
 * Generates PDF documents for check printing using the rendered check data
 * from the checks-print API. Handles layout positioning and element rendering.
 */
class CheckPDFGenerator {
  private pdf: jsPDF | null = null;
  private options: Required<PDFGeneratorOptions>;

  constructor(options: PDFGeneratorOptions = {}) {
    this.options = {
      showTestWatermark: options.showTestWatermark ?? false,
      pageWidth: options.pageWidth ?? 8.5,
      pageHeight: options.pageHeight ?? 11,
    };
  }

  /**
   * Generate PDF from rendered checks and template config
   */
  generatePDF(checks: RenderedCheck[], template: TemplateConfig): Blob {
    // Create new PDF document (letter size in inches)
    this.pdf = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: [this.options.pageWidth, template.pageHeight || this.options.pageHeight],
    });

    // Render each check on its own page
    checks.forEach((check, index) => {
      if (index > 0) {
        this.pdf!.addPage();
      }

      this.renderCheckPage(check, template);
    });

    // Return as blob for download/display
    return this.pdf.output("blob");
  }

  /**
   * Generate PDF and open in new tab for preview
   */
  generateAndPreview(checks: RenderedCheck[], template: TemplateConfig): void {
    const blob = this.generatePDF(checks, template);
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  }

  /**
   * Generate PDF and trigger download
   */
  generateAndDownload(
    checks: RenderedCheck[],
    template: TemplateConfig,
    filename = "checks.pdf"
  ): void {
    const blob = this.generatePDF(checks, template);
    downloadFile(blob, filename);
  }

  /**
   * Render a single check page
   */
  private renderCheckPage(check: RenderedCheck, template: TemplateConfig): void {
    if (!this.pdf) return;

    // Apply global offsets (element x/y are absolute page coordinates, not margin-relative)
    const offsetX = template.offsetX;
    const offsetY = template.offsetY;

    // Render test watermark if enabled
    if (this.options.showTestWatermark) {
      this.renderTestWatermark(template);
    }

    // Group elements by section
    const topElements = check.elements.filter((el) => el.section === "top");
    const middleElements = check.elements.filter((el) => el.section === "middle");
    const bottomElements = check.elements.filter((el) => el.section === "bottom");

    // Calculate section Y offsets based on layout type
    const sectionOffsets = this.calculateSectionOffsets(template);

    // Render each section
    topElements.forEach((el) => {
      this.renderElement(el, offsetX, offsetY + sectionOffsets.top);
    });

    middleElements.forEach((el) => {
      this.renderElement(el, offsetX, offsetY + sectionOffsets.middle);
    });

    bottomElements.forEach((el) => {
      this.renderElement(el, offsetX, offsetY + sectionOffsets.bottom);
    });
  }

  /**
   * Calculate Y offsets for each section based on layout type
   */
  private calculateSectionOffsets(template: TemplateConfig): {
    top: number;
    middle: number;
    bottom: number;
  } {
    const { layoutType, stubTopHeight, checkHeight, stubBottomHeight } = template;

    switch (layoutType) {
      case "check-top":
        // Check at top, stubs below
        return {
          top: 0, // Check section
          middle: checkHeight, // First stub
          bottom: checkHeight + stubTopHeight, // Second stub
        };

      case "check-middle":
        // Stub top, check middle, stub bottom
        return {
          top: 0, // Top stub
          middle: stubTopHeight, // Check section
          bottom: stubTopHeight + checkHeight, // Bottom stub
        };

      case "check-bottom":
        // Stubs at top, check at bottom
        return {
          top: 0, // First stub
          middle: stubTopHeight, // Second stub
          bottom: stubTopHeight + stubBottomHeight, // Check section
        };

      default:
        // Default to check-middle layout
        return {
          top: 0,
          middle: stubTopHeight,
          bottom: stubTopHeight + checkHeight,
        };
    }
  }

  /**
   * Render a single element to the PDF
   */
  private renderElement(
    element: RenderedCheckElement,
    offsetX: number,
    offsetY: number
  ): void {
    if (!this.pdf || !element.visible) return;

    const value = element.value;
    if (!value) return;

    // Calculate absolute position
    const x = offsetX + element.x;
    const y = offsetY + element.y;

    // Set font properties
    const fontStyle = element.fontWeight === "bold" ? "bold" : "normal";
    this.pdf.setFontSize(element.fontSize);
    this.pdf.setFont("helvetica", fontStyle);

    // Calculate text alignment offset
    let alignedX = x;
    if (element.alignment === "center") {
      alignedX = x + element.width / 2;
    } else if (element.alignment === "right") {
      alignedX = x + element.width;
    }

    // Map alignment to jsPDF align option
    const align = element.alignment === "center" ? "center" : element.alignment;

    // Render text with proper alignment
    this.pdf.text(value, alignedX, y, {
      align: align as "left" | "center" | "right",
      maxWidth: element.width,
    });
  }

  /**
   * Render test print watermark
   */
  private renderTestWatermark(template: TemplateConfig): void {
    if (!this.pdf) return;

    const pageHeight = template.pageHeight || this.options.pageHeight;
    const pageWidth = this.options.pageWidth;

    // Save current state
    const currentFontSize = this.pdf.getFontSize();

    // Set watermark style
    this.pdf.setFontSize(72);
    this.pdf.setTextColor(255, 0, 0);
    this.pdf.setGState(new (this.pdf as unknown as { GState: new (opts: { opacity: number }) => unknown }).GState({ opacity: 0.2 }));

    // Rotate and position watermark
    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;

    this.pdf.text("TEST PRINT", centerX, centerY, {
      align: "center",
      angle: 45,
    });

    // Restore state
    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setGState(new (this.pdf as unknown as { GState: new (opts: { opacity: number }) => unknown }).GState({ opacity: 1 }));
    this.pdf.setFontSize(currentFontSize);
  }

  /**
   * Draw section boundaries (for debugging/preview)
   */
  drawSectionBoundaries(template: TemplateConfig): void {
    if (!this.pdf) return;

    const pageWidth = this.options.pageWidth;
    const sectionOffsets = this.calculateSectionOffsets(template);

    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.setLineDashPattern([0.05, 0.05], 0);

    // Draw horizontal lines at section boundaries
    const boundaries = [
      sectionOffsets.top,
      sectionOffsets.middle,
      sectionOffsets.bottom,
      template.pageHeight,
    ];

    boundaries.forEach((y) => {
      this.pdf!.line(0, y, pageWidth, y);
    });
  }
}

export default CheckPDFGenerator;
